import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { RoundRobinVisual, SLATimerVisual, AuditLogVisual, HeroRoutingDiagram } from '../components/Diagrams';
import { Filter, Users, Clock, Shield, Zap, GitMerge } from 'lucide-react';
function FeatureBlock({
  title,
  description,
  icon: Icon,
  visual,
  reversed = false
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  visual: React.ReactNode;
  reversed?: boolean;
}) {
  return <div className={`flex flex-col gap-12 lg:items-center py-16 ${reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'}`} data-id="element-357">
      <div className="flex-1 space-y-6" data-id="element-358">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600" data-id="element-359">
          <Icon size={24} data-id="element-360" />
        </div>
        <h3 className="text-2xl font-bold tracking-tight text-slate-900" data-id="element-361">
          {title}
        </h3>
        <p className="text-lg text-slate-600 leading-relaxed" data-id="element-362">{description}</p>
      </div>
      <div className="flex-1 w-full" data-id="element-363">{visual}</div>
    </div>;
}
export function Product() {
  return <div className="flex flex-col w-full bg-white" data-id="element-364">
      {/* Header */}
      <section className="pt-20 pb-16 bg-slate-50 border-b border-slate-200" data-id="element-365">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-366">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-6" data-id="element-367">
            Everything you need to route leads perfectly
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-368">
            A complete toolkit for RevOps to manage complex assignment logic,
            enforce SLAs, and maintain total visibility.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-12" data-id="element-369">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-370">
          <FeatureBlock title="Global Routing Logic" description="Stop building dozens of fragmented workflows. DemandPath gives you a centralized, visual ruleset to evaluate every inbound lead against your global logic, checking geography, company size, and custom properties in one place." icon={Filter} visual={<HeroRoutingDiagram data-id="element-372" />} data-id="element-371" />

          <FeatureBlock title="Advanced Round Robin" description="Distribute leads fairly with intelligent round robin pools. Set custom weightings for ramping reps, respect working hours and timezones, and automatically pause distribution when reps are out of office." icon={Users} visual={<RoundRobinVisual data-id="element-374" />} reversed data-id="element-373" />

          <FeatureBlock title="Owner-Aware & Named Accounts" description="Never accidentally route a target account to the wrong rep. DemandPath natively checks for existing contact owners, company owners, or named account lists before applying general routing rules." icon={GitMerge} visual={<div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-376">
                <div className="space-y-4" data-id="element-377">
                  <div className="bg-white p-4 rounded-lg border border-brand-200 shadow-sm relative" data-id="element-378">
                    <div className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-brand-100 border border-brand-200 flex items-center justify-center text-xs font-bold text-brand-700" data-id="element-379">
                      1
                    </div>
                    <div className="text-sm font-semibold text-slate-900" data-id="element-380">
                      Check Existing Open Deal
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-381">
                      If true → Route to Deal Owner
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm relative" data-id="element-382">
                    <div className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-500" data-id="element-383">
                      2
                    </div>
                    <div className="text-sm font-semibold text-slate-900" data-id="element-384">
                      Check Named Account List
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-385">
                      If true → Route to Target Account Owner
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm relative" data-id="element-386">
                    <div className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-500" data-id="element-387">
                      3
                    </div>
                    <div className="text-sm font-semibold text-slate-900" data-id="element-388">
                      Apply Territory Rules
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-389">
                      Fallback to standard routing
                    </div>
                  </div>
                </div>
              </div>} data-id="element-375" />

          <FeatureBlock title="SLA Enforcement" description="Speed to lead matters. Start SLA timers the moment a lead is assigned. If the rep doesn't respond in time, automatically trigger Slack escalations, re-assign the lead to the next available rep, and log the miss." icon={Clock} visual={<SLATimerVisual data-id="element-391" />} reversed data-id="element-390" />

          <FeatureBlock title="Audit Trail & Explainability" description="No more guessing why a lead went to the wrong person. DemandPath logs every routing decision, showing exactly which rule matched, who it was assigned to, and why, giving RevOps total confidence." icon={Shield} visual={<AuditLogVisual data-id="element-393" />} data-id="element-392" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-900 text-white mt-12" data-id="element-394">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-395">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6" data-id="element-396">
            Ready to clean up your lead routing?
          </h2>
          <p className="text-lg text-brand-100 mb-10" data-id="element-397">
            Join the RevOps teams using DemandPath to orchestrate inbound leads
            in HubSpot.
          </p>
          <Button href="/book-a-demo" variant="primary" size="lg" className="bg-white text-brand-900 hover:bg-slate-50" data-id="element-398">
            Book a demo
          </Button>
        </div>
      </section>
    </div>;
}